import {
  CButton,
  CCard,
  CCardBody,
  CCardGroup,
  CCardHeader,
  CCol,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CFormTextarea,
  CRow,
} from "@coreui/react";
import React, { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  checkPublicPickupLead,
  getPublicDeliveryOffices,
  submitPublicPickup,
} from "src/api/commonapi";
import CustomImage from "src/views/CommonFunctions/CustomImage";
import { Routes } from "src/constants/api_url";
import { notification } from "src/constants/message";
import moment from "moment";
import {
  BORZO_COURIER_ID,
  BLUEDART_COURIER_ID,
  resolveCourierByCity,
} from "src/views/CommonFunctions/common";
import TimeSlotSelect from "src/views/CommonFunctions/TimeSlotSelect";

const toDDMMYYYY = (dateStr) => {
  if (!dateStr) return "";
  const parts = dateStr.split("-");
  if (parts.length !== 3) return "";
  return `${parts[2]}-${parts[1]}-${parts[0]}`;
};

const formSchema = yup.object({
  email: yup
    .string()
    .trim()
    .email("Please enter a valid email address")
    .required("Email is required"),
  contact_name: yup.string().trim().required("Contact person name is required"),
  mobile: yup.string().trim().required("Mobile number is required"),
  address: yup.string().trim().required("Address is required"),
  city: yup.string().trim().required("City is required"),
  pincode: yup.string().trim().required("Pincode is required"),
  collection_date: yup.string().trim().required("Collection date is required"),
  from_time: yup.string().when("$borzo_enabled", {
    is: 1,
    then: (schema) =>
      schema.required("From time is required for Borzo courier"),
    otherwise: (schema) => schema.notRequired(),
  }),
  to_time: yup.string().when("$borzo_enabled", {
    is: 1,
    then: (schema) => schema.required("To time is required for Borzo courier"),
    otherwise: (schema) => schema.notRequired(),
  }),
  pickup_location_link: yup.string().when("$borzo_enabled", {
    is: 1,
    then: (schema) =>
      schema.required("Location link is required for Borzo courier"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const defaultValues = {
  email: "",
  contact_name: "",
  mobile: "",
  address: "",
  city: "",
  pincode: "",
  collection_date: "",
  from_time: "",
  to_time: "",
  pickup_location_link: "",
};

const PickupRequestForm = () => {
  const [loading, setLoading] = useState(false);
  const [leadVerified, setLeadVerified] = useState(false);
  const [leadData, setLeadData] = useState(null);
  const [offices, setOffices] = useState([]);
  const [submitResponse, setSubmitResponse] = useState(null);
  const [holders, setHolders] = useState([]);
  const [pickupDone, setPickupDone] = useState(null);
  const [submittedCollectionDate, setSubmittedCollectionDate] = useState("");
  const [resolvedCourier, setResolvedCourier] = useState({
    courier_id: BLUEDART_COURIER_ID,
    courier_name: "Blue Dart Courier",
    matched_office: null,
    borzo_enabled: 0,
    start_time: "",
    end_time: "",
  });

  let prefixoption = [
    { value: 3, label: "Dr." },
    { value: 4, label: "M/S" },
    { value: 5, label: "Master" },
    { value: 2, label: "Miss." },
    { value: 0, label: "Mr." },
    { value: 1, label: "Mrs." },
    { value: 6, label: ":" },
  ];

  const methods = useForm({
    resolver: yupResolver(formSchema),
    defaultValues,
    context: { borzo_enabled: resolvedCourier.borzo_enabled },
  });

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    trigger,
    formState: { errors },
  } = methods;

  useEffect(() => {
    loadOffices();
  }, []);

  const timeToMinutes = (t) => {
    if (!t) return -1;
    const match = t.match(/^(\d+):(\d+)\s*(AM|PM)$/i);
    if (!match) return -1;
    let hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const period = match[3].toUpperCase();
    if (period === "PM" && hours !== 12) hours += 12;
    if (period === "AM" && hours === 12) hours = 0;
    return hours * 60 + minutes;
  };

  const collectionDate = watch("collection_date");
  useEffect(() => {
    if (!collectionDate) return;
    const today = moment().format("YYYY-MM-DD");
    if (collectionDate === today) {
      const nowMin = timeToMinutes(moment().format("h:mm A"));
      const fromTime = getValues("from_time");
      const toTime = getValues("to_time");
      if (fromTime && timeToMinutes(fromTime) < nowMin) {
        setValue("from_time", "");
        setValue("to_time", "");
      } else if (toTime && timeToMinutes(toTime) < nowMin) {
        setValue("to_time", "");
      }
    }
  }, [collectionDate]);

  const loadOffices = async () => {
    try {
      const resp = await getPublicDeliveryOffices();
      if (resp?.status == 1) {
        setOffices(resp.data || []);
      }
    } catch (e) {
      console.log("loadOffices", e);
    }
  };

  const resolveCity = (val) => {
    const resolved = resolveCourierByCity(val, offices);
    setResolvedCourier(resolved);
    if (resolved.courier_id !== BORZO_COURIER_ID) {
      setValue("from_time", "");
      setValue("to_time", "");
      setValue("pickup_location_link", "");
    }
    trigger("from_time");
    trigger("to_time");
    trigger("pickup_location_link");
  };

  const isSunday = (dateStr) => {
    if (!dateStr) return false;
    const m = moment(dateStr, "YYYY-MM-DD", true);
    return m.isValid() && m.day() === 0;
  };

  const isPastDate = (dateStr) => {
    if (!dateStr) return false;
    const m = moment(dateStr, "YYYY-MM-DD", true);
    const isBlueDart = resolvedCourier.courier_id === BLUEDART_COURIER_ID;
    const after2PM = moment().hour() >= 14;
    if (isBlueDart && after2PM) {
      return !m.isAfter(moment(), "day");
    }
    return m.isBefore(moment().startOf("day"));
  };

  const getMinDate = () => {
    const now = moment();
    const isBlueDart = resolvedCourier.courier_id === BLUEDART_COURIER_ID;
    const after2PM = now.hour() >= 14;
    if (isBlueDart && after2PM) {
      let next = moment().add(1, "day");
      while (next.day() === 0) {
        next.add(1, "day");
      }
      return next.format("YYYY-MM-DD");
    }
    return now.format("YYYY-MM-DD");
  };

  const adjustTimeSlot = (timeStr, offsetMinutes) => {
    if (!timeStr) return "";
    const m = moment(timeStr, ["h:mm A", "hh:mm A", "H:mm", "HH:mm"]);
    if (!m.isValid()) return "";
    m.add(offsetMinutes, "minutes");
    return m.format("h:mm A");
  };

  const getEffectiveStartTime = () => {
    return resolvedCourier.start_time || undefined;
  };

  const getDisableBefore = () => {
    const selectedDate = watch("collection_date");
    const today = moment().format("YYYY-MM-DD");
    if (selectedDate && selectedDate === today) {
      return moment().format("h:mm A");
    }
    return undefined;
  };

  const showToast = (type, message) => {
    notification({ type, message })();
  };

  const handleEmailBlur = async () => {
    const email = getValues("email");
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      trigger("email");
      return;
    }
    setLoading(true);
    try {
      const resp = await checkPublicPickupLead({ email: email.trim() });
      if (resp?.status == 1) {
        if (resp.data.pickup_done == 1) {
          setPickupDone(resp.data);
          setLoading(false);
          return;
        }
        setLeadData(resp.data);
        setLeadVerified(true);
        // If existing pickup data, pre-fill with submitted values
        const ep = resp.data.existing_pickup;
        if (ep) {
          setValue("contact_name", ep.contact_name || "");
          setValue("mobile", ep.mobile || "");
          setValue("address", ep.address || "");
          setValue("city", ep.city || "");
          setValue("pincode", ep.pincode || "");
          setValue("collection_date", ep.collection_date || "");
          setValue("from_time", ep.from_time || "");
          setValue("to_time", ep.to_time || "");
          setValue("pickup_location_link", ep.pickup_location_link || "");
          if (ep.city) {
            const resolved = resolveCourierByCity(ep.city, offices);
            setResolvedCourier(resolved);
          }
        } else {
          setValue(
            "contact_name",
            resp.data.l_name_prefix
              ? `${resp.data.l_name_prefix} ${resp.data.l_name}`
              : resp.data.l_name || "",
          );
          setValue("mobile", resp.data.co_no || "");
          setValue("address", resp.data.l_address || "");
          setValue("city", resp.data.l_city || "");
          setValue("pincode", resp.data.l_pincode || "");
          if (resp.data.l_city) {
            const resolved = resolveCourierByCity(resp.data.l_city, offices);
            setResolvedCourier(resolved);
          }
        }
        const initialHolders = (resp.data.applicants || []).map((a) => ({
          prefix: a.prefix || ":",
          applicant_name: a.applicant_name || "",
          document_name: a.document_name || "",
        }));
        setHolders(
          initialHolders.length > 0
            ? initialHolders
            : [{ prefix: ":", applicant_name: "", document_name: "" }],
        );
      } else {
        showToast("error", resp?.message || "Email not found");
      }
    } catch (e) {
      console.log("checkEmail", e);
      showToast("error", "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (formData) => {
    if (isSunday(formData.collection_date)) {
      showToast("error", "Sunday is not available for collection");
      return;
    }
    if (isPastDate(formData.collection_date)) {
      showToast("error", "Collection date must be a future date");
      return;
    }
    for (let i = 0; i < holders.length; i++) {
      if (
        !holders[i].applicant_name ||
        holders[i].applicant_name.trim() === ""
      ) {
        showToast(
          "error",
          `Document holder's name is required for ${holders[i].document_name || `applicant ${i + 1}`}`,
        );
        return;
      }
    }
    setSubmittedCollectionDate(formData.collection_date);
    setLoading(true);
    try {
      const payload = {
        lead_id: leadData.lead_id,
        email: leadData.l_email,
        contact_name: formData.contact_name.trim(),
        mobile: formData.mobile.trim(),
        address: formData.address.trim(),
        city: formData.city.trim(),
        pincode: formData.pincode.trim(),
        collection_date: toDDMMYYYY(formData.collection_date),
        from_time: formData.from_time || "",
        to_time: formData.to_time || "",
        pickup_location_link: formData.pickup_location_link || "",
        courier_id: resolvedCourier.courier_id,
        courier_address: resolvedCourier.matched_office?.attribute_id || 0,
        holders: holders.map((h) => ({
          prefix: h.prefix,
          applicant_name: h.applicant_name.trim(),
          document_name: h.document_name,
        })),
      };
      const resp = await submitPublicPickup(payload);
      if (resp?.status == 1) {
        setSubmitResponse(resp);
      } else {
        showToast("error", resp?.message || "Failed to submit pickup request");
      }
    } catch (e) {
      console.log("submit", e);
      showToast("error", "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleHolderChange = (index, field, value) => {
    const updated = [...holders];
    updated[index][field] = value;
    setHolders(updated);
  };

  if (pickupDone) {
    return (
      <div
        className="d-flex justify-content-center "
        style={{ background: "#f5f7fa", padding: "10px" }}
      >
        <CCard style={{ width: "950px" }}>
          <CCardHeader className="text-center">
            <CustomImage
              src={`${Routes.API_HOSTNAME}/images/adminlogo/loginlogo.png`}
              width="100px"
              height="100px"
              style={{ objectFit: "contain" }}
            />
            <h4
              style={{ color: "#dc3545", fontSize: "22px", marginTop: "15px" }}
            >
              {pickupDone.pickup_error
                ? "Pickup Completed"
                : `Your pickup is already ${pickupDone.pickup_status || "registered"}`}
            </h4>
            {pickupDone.pickup_error ? (
              <p style={{ color: "black", fontSize: "14px" }}>
                {pickupDone.pickup_error}
              </p>
            ) : (
              <p style={{ color: "black", fontSize: "14px" }}>
                You cannot submit another pickup request at this time.
              </p>
            )}
          </CCardHeader>
          {pickupDone.pickup_template && (
            <CCardBody>
              <div
                className="border rounded p-3"
                style={{ background: "#fff", fontSize: "14px" }}
                dangerouslySetInnerHTML={{ __html: pickupDone.pickup_template }}
              />
            </CCardBody>
          )}
        </CCard>
      </div>
    );
  }

  if (submitResponse) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ background: "#f5f7fa", padding: "20px", height: "100vh" }}
      >
        <CCard
          style={{
            width: "950px",
            borderRadius: "12px",
            boxShadow: "0 2px 12px rgba(0,0,0,0.1)",
          }}
        >
          <CCardHeader className="text-center">
            <CustomImage
              src={`${Routes.API_HOSTNAME}/images/adminlogo/loginlogo.png`}
              width="120px"
              height="120px"
              style={{ objectFit: "contain" }}
            />
          </CCardHeader>
          <CCardBody style={{ padding: "10px 30px 30px" }}>
            <div style={{ textAlign: "center" }}>
              <h3
                style={{
                  color: "#28a745",
                  fontSize: "28px",
                  fontWeight: "bold",
                  marginBottom: "10px",
                }}
              >
                Thank You!
              </h3>
              <h5
                style={{
                  color: "#333",
                  fontSize: "20px",
                  fontWeight: "500",
                  marginBottom: "20px",
                }}
              >
                Pickup request submitted successfully.
              </h5>
              {submitResponse?.data?.courier_name && (
                <p
                  style={{
                    color: "#333",
                    fontSize: "17px",
                    marginBottom: "10px",
                  }}
                >
                  Courier Company:{" "}
                  <strong>
                    {submitResponse.data.courier_id === 754
                      ? "Borzo(WeFast)/Porter Courier"
                      : submitResponse.data.courier_name}
                  </strong>
                </p>
              )}
              {submitResponse?.data?.proforma == 1 && (
                <p
                  style={{
                    color: "#333",
                    fontSize: "17px",
                    marginBottom: "10px",
                  }}
                >
                  invoice will be shared on your email.
                </p>
              )}
              <p
                style={{
                  color: "#333",
                  fontSize: "17px",
                  marginBottom: "10px",
                }}
              >
                {(() => {
                  const base = "You will receive the pickup details via email";
                  const isBorzo = submitResponse?.data?.courier_id === 754;
                  if (!submittedCollectionDate) return `${base} shortly.`;
                  const now = moment();
                  const today = now.format("YYYY-MM-DD");
                  const selected = moment(
                    submittedCollectionDate,
                    "YYYY-MM-DD",
                  );
                  const isToday = submittedCollectionDate === today;
                  const isTomorrow =
                    selected.clone().subtract(1, "day").format("YYYY-MM-DD") ===
                    today;
                  const isMonday = selected.day() === 1;
                  const timeSuffix = isBorzo
                    ? ""
                    : isMonday
                      ? " morning"
                      : " between 5 to 7pm at evening";
                  const emailDate =
                    isToday || isTomorrow
                      ? "today"
                      : isMonday
                        ? selected.format("DD-MM-YYYY")
                        : selected
                            .clone()
                            .subtract(1, "day")
                            .format("DD-MM-YYYY");
                  if (isToday && now.hour() < 14) return `${base} shortly.`;
                  return `${base} on ${emailDate}${timeSuffix}.`;
                })()}
              </p>
            </div>
          </CCardBody>
        </CCard>
      </div>
    );
  }

  return (
    <div
      className="d-flex justify-content-center "
      style={{ background: "#f5f7fa", padding: "10px" }}
    >
      <CCard style={{ width: "950px" }}>
        <CCardHeader className="text-center">
          <CustomImage
            src={`${Routes.API_HOSTNAME}/images/adminlogo/loginlogo.png`}
            width="100px"
            height="100px"
            style={{ objectFit: "contain" }}
          />
          <h5
            className="form_title mb-0"
            style={{ color: "black", fontSize: "18px" }}
          >
            Fill out the form below to schedule your document pickup.
          </h5>
          {!leadVerified && (
            <p className="text-muted" style={{ fontSize: "13px" }}>
              Enter your email to start
            </p>
          )}
        </CCardHeader>
        <CCardBody>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div
                className="mb-3"
                style={{
                  background: "#e8f5e9",
                  padding: "15px",
                  borderRadius: "8px",
                }}
              >
                <CFormLabel style={{ color: "black" }}>
                  Email Address
                </CFormLabel>
                <CRow>
                  <CCol xs={12} md>
                    <Controller
                      name="email"
                      control={control}
                      render={({ field }) => (
                        <CFormInput
                          {...field}
                          type="email"
                          placeholder="Enter your email"
                          disabled={leadVerified}
                        />
                      )}
                    />
                  </CCol>
                  <CCol xs={12} md="auto" className="mt-2 mt-md-0">
                    <CButton
                      color="primary"
                      disabled={loading || leadVerified}
                      onClick={handleEmailBlur}
                      className="w-100"
                    >
                      {loading ? "Checking..." : "Submit"}
                    </CButton>
                  </CCol>
                </CRow>
                {errors?.email && (
                  <span className="text-danger">{errors?.email?.message}</span>
                )}
              </div>

              {leadVerified && (
                <>
                  <hr />
                  <div
                    style={{
                      background: "#fff3e0",
                      padding: "15px",
                      borderRadius: "8px",
                    }}
                    className="mb-3"
                  >
                    <CRow>
                      <CCol md={6}>
                        <CFormLabel style={{ color: "black" }}>
                          Contact Person Name{" "}
                          <span style={{ color: "red" }}>*</span>
                        </CFormLabel>
                        <Controller
                          name="contact_name"
                          control={control}
                          render={({ field }) => (
                            <CFormInput
                              {...field}
                              placeholder="Contact person name"
                            />
                          )}
                        />
                        {errors?.contact_name && (
                          <span className="text-danger">
                            {errors?.contact_name?.message}
                          </span>
                        )}
                      </CCol>
                      <CCol md={6}>
                        <CFormLabel style={{ color: "black" }}>
                          Mobile No <span style={{ color: "red" }}>*</span>
                        </CFormLabel>
                        <Controller
                          name="mobile"
                          control={control}
                          render={({ field }) => (
                            <CFormInput
                              {...field}
                              placeholder="Mobile number"
                            />
                          )}
                        />
                        {errors?.mobile && (
                          <span className="text-danger">
                            {errors?.mobile?.message}
                          </span>
                        )}
                      </CCol>
                    </CRow>
                  </div>

                  <div
                    className="mb-3"
                    style={{
                      background: "#fff8e1",
                      padding: "15px",
                      borderRadius: "8px",
                    }}
                  >
                    <CFormLabel style={{ color: "black" }}>
                      Address <span style={{ color: "red" }}>*</span>
                    </CFormLabel>
                    <Controller
                      name="address"
                      control={control}
                      render={({ field }) => (
                        <CFormTextarea
                          {...field}
                          placeholder="Address"
                          rows={3}
                        />
                      )}
                    />
                    {errors?.address && (
                      <span className="text-danger">
                        {errors?.address?.message}
                      </span>
                    )}

                    <CRow className="mt-3">
                      <CCol md={6}>
                        <CFormLabel style={{ color: "black" }}>
                          City <span style={{ color: "red" }}>*</span>
                        </CFormLabel>
                        <Controller
                          name="city"
                          control={control}
                          render={({ field }) => (
                            <CFormInput
                              {...field}
                              placeholder="City"
                              onBlur={() => resolveCity(field.value)}
                            />
                          )}
                        />
                        {errors?.city && (
                          <span className="text-danger">
                            {errors?.city?.message}
                          </span>
                        )}
                      </CCol>
                      <CCol md={6}>
                        <CFormLabel style={{ color: "black" }}>
                          Pincode <span style={{ color: "red" }}>*</span>
                        </CFormLabel>
                        <Controller
                          name="pincode"
                          control={control}
                          render={({ field }) => (
                            <CFormInput {...field} placeholder="Pincode" />
                          )}
                        />
                        {errors?.pincode && (
                          <span className="text-danger">
                            {errors?.pincode?.message}
                          </span>
                        )}
                      </CCol>
                    </CRow>

                    {resolvedCourier.borzo_enabled == 1 && (
                      <div className="mt-3">
                        <CFormLabel style={{ color: "black" }}>
                          Location Link <span style={{ color: "red" }}>*</span>
                        </CFormLabel>
                        <Controller
                          name="pickup_location_link"
                          control={control}
                          render={({ field }) => (
                            <CFormInput
                              {...field}
                              placeholder="Paste Google Maps or location URL"
                            />
                          )}
                        />
                        {errors?.pickup_location_link && (
                          <span className="text-danger">
                            {errors?.pickup_location_link?.message}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  <hr />

                  <div
                    style={{
                      background: "#f3e5f5",
                      padding: "15px",
                      borderRadius: "8px",
                    }}
                    className="mb-3"
                  >
                    <CFormLabel style={{ color: "black" }}>
                      Collection Date <span style={{ color: "red" }}>*</span>
                    </CFormLabel>
                    <Controller
                      name="collection_date"
                      control={control}
                      render={({ field }) => (
                        <CFormInput
                          {...field}
                          type="date"
                          min={getMinDate()}
                          value={field.value || ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (
                              val &&
                              moment(val, "YYYY-MM-DD", true).day() === 0
                            ) {
                              showToast(
                                "error",
                                "Sunday is not available for collection",
                              );
                              field.onChange("");
                              return;
                            }
                            field.onChange(val);
                          }}
                        />
                      )}
                    />
                    {errors?.collection_date && (
                      <span className="text-danger">
                        {errors?.collection_date?.message}
                      </span>
                    )}

                    {resolvedCourier.borzo_enabled == 1 && (
                      <CRow className="mt-3">
                        <CCol md={6}>
                          <Controller
                            name="from_time"
                            control={control}
                            render={({ field }) => (
                              <TimeSlotSelect
                                label="From Time"
                                required
                                value={field.value}
                                onChange={(val) => {
                                  field.onChange(val);
                                  if (val) {
                                    const autoTo = adjustTimeSlot(val, 60);
                                    const maxTo = resolvedCourier.end_time;
                                    if (autoTo && maxTo && autoTo > maxTo) {
                                      setValue("to_time", maxTo);
                                    } else if (autoTo) {
                                      setValue("to_time", autoTo);
                                    }
                                  }
                                }}
                                placeholder="Select From Time"
                                startTime={getEffectiveStartTime()}
                                endTime={
                                  adjustTimeSlot(
                                    resolvedCourier.end_time,
                                    -30,
                                  ) || undefined
                                }
                                disableBefore={getDisableBefore()}
                              />
                            )}
                          />
                          {errors?.from_time && (
                            <span className="text-danger">
                              {errors?.from_time?.message}
                            </span>
                          )}
                        </CCol>
                        <CCol md={6}>
                          <Controller
                            name="to_time"
                            control={control}
                            render={({ field }) => (
                              <TimeSlotSelect
                                label="To Time"
                                required
                                value={field.value}
                                onChange={field.onChange}
                                placeholder="Select To Time"
                                startTime={
                                  adjustTimeSlot(getEffectiveStartTime(), 30) ||
                                  undefined
                                }
                                endTime={resolvedCourier.end_time || undefined}
                                disableBefore={
                                  adjustTimeSlot(getDisableBefore(), 30) ||
                                  undefined
                                }
                              />
                            )}
                          />
                          {errors?.to_time && (
                            <span className="text-danger">
                              {errors?.to_time?.message}
                            </span>
                          )}
                        </CCol>
                      </CRow>
                    )}
                  </div>
                  <hr />
                  <div
                    style={{
                      background: "#e0e9ff",
                      padding: "15px",
                      borderRadius: "8px",
                    }}
                    className="mb-3"
                  >
                    <h6 style={{ color: "black", marginBottom: "10px" }}>
                      Enter the Name of the Document Holder
                    </h6>
                    {holders.map((h, idx) => (
                      <CRow key={idx} className="mb-2 align-items-end">
                        <CCol md={6}>
                          <CFormLabel
                            style={{ color: "black", fontSize: "13px" }}
                          >
                            {h.document_name || `Applicant ${idx + 1}`}{" "}
                            <span style={{ color: "red" }}>*</span>
                          </CFormLabel>
                        </CCol>
                        <CCol md={6}>
                          <CFormInput
                            value={h.applicant_name}
                            placeholder="Enter name"
                            onChange={(e) =>
                              handleHolderChange(
                                idx,
                                "applicant_name",
                                e.target.value,
                              )
                            }
                          />
                        </CCol>
                      </CRow>
                    ))}
                  </div>

                  <div className="text-center mt-4 mb-3">
                    <CButton type="submit" color="primary" disabled={loading}>
                      {loading ? "Submitting..." : "Submit Pickup Request"}
                    </CButton>
                  </div>
                </>
              )}
            </form>
          </FormProvider>
        </CCardBody>
      </CCard>
    </div>
  );
};

export default PickupRequestForm;
